import React, { useMemo, useState } from 'react';
import { View, ScrollView, Pressable, Image, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import {
  OUTPUT_TYPE_ICON,
  formatTimeAgo,
  getFileUrl,
  getRunStatusVisual,
  summarizeRunInputs,
  toolRunResultPreviewSnippet,
  resolveToolMediaPreviewAspectRatio,
} from '@/components/Tools/toolDetailHelpers';

/**
 * Horizontal scroll strip of recent ToolRuns. Replaces the old vertical
 * sidebar list with rich preview tiles — each run gets a real thumbnail
 * (image, video frame, text snippet, or output-type icon), an inline status
 * chip, hover-to-delete, and a clear active state.
 */
function ToolRecentRunsStrip({
  runs,
  activeRunId,
  outputType,
  palette,
  deletingRunId,
  onSelect,
  onDelete,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const styles = useMemo(() => StyleSheet.create({
    scroll: {
      marginHorizontal: -24,
    },
    scrollContent: {
      flexDirection: 'row',
      gap: 14,
      paddingHorizontal: 24,
      paddingVertical: 4,
    },
    empty: {
      paddingVertical: 20,
      paddingHorizontal: 18,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },
  }), [colors]);

  if (!runs?.length) {
    return (
      <View style={styles.empty}>
        <Text size='s' color='secondary'>
          {t('pages.tools.noRunsHint')
            || 'No runs yet. Your past results will appear here so you can revisit, re-run, or continue them in chat.'}
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
    >
      {runs.map((run) => (
        <RunCard
          key={run.id}
          run={run}
          isActive={run.id === activeRunId}
          isDeleting={deletingRunId === run.id}
          outputType={outputType}
          palette={palette}
          colors={colors}
          onSelect={onSelect}
          onDelete={onDelete}
          t={t}
        />
      ))}
    </ScrollView>
  );
}

// ---------------------------------------------------------------------------
// RunCard
//
// Visually rich run tile:
//  - Top: media preview (image, video, text snippet, or icon fallback) with a
//    bottom gradient scrim so the status chip stays legible over photos.
//  - Status chip in the bottom-left corner of the thumbnail with the proper
//    color + icon + label per ToolRun.status (Done / Failed / Running / etc).
//  - Hover-only delete button in the top-right.
//  - Active state: accent border + a small "Viewing" pill in the top-left.
//  - Bottom: tool name + relative timestamp + credit cost.
// ---------------------------------------------------------------------------
function RunCard({ run, isActive, isDeleting, outputType, palette, colors, onSelect, onDelete, t }) {
  // Tracked locally so the delete button doesn't unmount whenever the outer
  // Pressable's `hovered` render-prop flickers (which it does on web when
  // the cursor moves onto a nested Pressable such as the delete button
  // itself — combined with the card's `translateY(-2)` hover transform
  // that effect made the trash icon impossible to click: it would appear,
  // then immediately disappear from under the cursor).
  const [hovered, setHovered] = useState(false);
  const firstFile = Array.isArray(run.resultFiles) ? run.resultFiles[0] : null;
  const fileUrl = getFileUrl(firstFile);
  const mime = firstFile?.mimeType || '';
  const isImage = mime.startsWith('image/');
  const isVideo = mime.startsWith('video/');
  const isAudio = mime.startsWith('audio/');
  // For non-image/video media (3D models, documents, etc.) the File model
  // serializes a poster as `thumbnailPath` (already an absolute CDN URL).
  // Surface it as the card preview so 3D / doc cards aren't all the same
  // generic icon. (`thumbnailUrl` / `previewUrl` are kept as fallbacks for
  // any older shape that might still be in the wild.)
  const posterUrl = !isImage && !isVideo
    ? (firstFile?.thumbnailPath || firstFile?.thumbnailUrl || firstFile?.previewUrl || null)
    : null;
  // Surface the user's actual inputs (e.g. "golden flower · glb") instead of
  // the compiled prompt — the compiled prompt is the template-substituted
  // payload and looks identical across runs of the same tool, which is
  // exactly what made every card here read as a duplicate.
  const inputSummary = summarizeRunInputs(run, 80);
  const previewText = toolRunResultPreviewSnippet(run, 120) || inputSummary || '';
  const status = getRunStatusVisual(run.status, colors, palette, t);
  const previewAspect = useMemo(
    () => ((isImage || isVideo) ? resolveToolMediaPreviewAspectRatio(firstFile, run, outputType) : null),
    [firstFile, run, outputType, isImage, isVideo],
  );

  const styles = useMemo(() => StyleSheet.create({
    card: {
      width: 230,
      borderRadius: 14,
      borderWidth: isActive ? 2 : 1,
      borderColor: isActive ? palette.fg : colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      overflow: 'hidden',
      ...(Platform.OS === 'web' ? { transition: 'transform 0.15s ease, border-color 0.15s ease' } : null),
    },
    cardHover: {
      ...(Platform.OS === 'web' ? { transform: [{ translateY: -2 }] } : null),
    },
    thumb: {
      width: '100%',
      backgroundColor: colors.solid_color_tertiary,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative',
    },
    thumbImage: {
      width: '100%',
      height: '100%',
    },
    scrim: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 60,
    },
    textPreview: {
      padding: 12,
      flex: 1,
      width: '100%',
      flexDirection: 'row',
      gap: 8,
    },
    textPreviewMark: {
      width: 3,
      borderRadius: 2,
      backgroundColor: palette.fg,
      opacity: 0.7,
    },
    statusChip: {
      position: 'absolute',
      bottom: 8,
      left: 8,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingVertical: 3,
      paddingHorizontal: 7,
      borderRadius: 999,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      ...(Platform.OS === 'web' ? { backdropFilter: 'blur(4px)' } : null),
    },
    activePill: {
      position: 'absolute',
      top: 8,
      left: 8,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingVertical: 3,
      paddingHorizontal: 7,
      borderRadius: 999,
      backgroundColor: palette.fg,
    },
    deleteBtn: {
      position: 'absolute',
      top: 6,
      right: 6,
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
      ...(Platform.OS === 'web'
        ? { backdropFilter: 'blur(4px)', transition: 'opacity 0.15s ease' }
        : null),
    },
    deleteBtnHidden: {
      opacity: 0,
      // pointerEvents on a style works on web via react-native-web and keeps
      // the element in the DOM so the card's hover state stays stable.
      pointerEvents: 'none',
    },
    body: {
      padding: 11,
      gap: 4,
    },
    metaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 6,
    },
  }), [colors, palette, isActive]);

  // On web we fade the delete button in on hover (or when a delete is in
  // flight). On native there's no hover, so it's always visible. The button
  // is ALWAYS rendered when `onDelete` is wired up — hiding it via opacity
  // instead of conditional mounting keeps the DOM element stable, which is
  // what actually allows the cursor to land on it without the parent card
  // re-rendering it out from under the pointer.
  const showDeleteBtn = Platform.OS !== 'web' || hovered || isDeleting;

  return (
    <Pressable
      onPress={() => onSelect?.(run)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole='button'
      accessibilityLabel={`View run from ${formatTimeAgo(run.createdAt)}`}
      style={[
        styles.card,
        hovered && styles.cardHover,
      ]}
    >
      <View style={[styles.thumb, previewAspect ? { aspectRatio: previewAspect } : { height: 130 }]}>
        {isImage && fileUrl ? (
          <Image source={{ uri: fileUrl }} style={styles.thumbImage} resizeMode='cover' />
        ) : isVideo && fileUrl && Platform.OS === 'web' ? (
          // eslint-disable-next-line react/forbid-elements
          <video src={fileUrl} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : posterUrl ? (
          <Image source={{ uri: posterUrl }} style={styles.thumbImage} resizeMode='cover' />
        ) : isAudio ? (
          <Icon name='waveform-lines' size={32} color={colors.text_color_tertiary} />
        ) : previewText ? (
          <View style={styles.textPreview}>
            <View style={styles.textPreviewMark} />
            <Text size='xs' color='secondary' numberOfLines={6} style={{ flex: 1, lineHeight: 16 }}>
              {previewText}
            </Text>
          </View>
        ) : (
          <Icon
            name={OUTPUT_TYPE_ICON[outputType] || 'wand-magic-sparkles'}
            size={32}
            color={colors.text_color_tertiary}
          />
        )}

        {/* Subtle gradient scrim so the status chip stays readable over
            bright photos. We only render it when there's actually
            pixel media (image/video) to scrim. */}
        {(isImage || isVideo || posterUrl) && (
          <LinearGradient
            colors={['transparent', 'rgba(0, 0, 0, 0.55)']}
            style={styles.scrim}
            pointerEvents='none'
          />
        )}

        <View style={styles.statusChip}>
          <Icon name={status.icon} size={9} color={status.color} solid />
          <Text size='xxs' weight='bold' style={{ color: '#fff' }}>
            {status.label}
          </Text>
        </View>

        {isActive && (
          <View style={styles.activePill}>
            <Icon name='eye' size={9} color='#fff' solid />
            <Text size='xxs' weight='bold' style={{ color: '#fff' }}>
              {t?.('pages.tools.viewing') || 'Viewing'}
            </Text>
          </View>
        )}

        {!!onDelete && (
          <Pressable
            onPress={(e) => {
              e?.stopPropagation?.();
              onDelete(run);
            }}
            onHoverIn={() => setHovered(true)}
            disabled={isDeleting}
            style={[
              styles.deleteBtn,
              !showDeleteBtn && styles.deleteBtnHidden,
            ]}
            hitSlop={6}
            accessibilityLabel='Delete run'
          >
            {isDeleting ? (
              <ActivityIndicator size='small' color='#fff' />
            ) : (
              <Icon name='trash' size={11} color='#fff' />
            )}
          </Pressable>
        )}
      </View>

      <View style={styles.body}>
        <Text size='s' color='primary' weight='bold' numberOfLines={1}>
          {run.tool?.name || run.toolSlug}
        </Text>
        {!!inputSummary && (
          <Text size='xs' color='secondary' numberOfLines={1}>
            {inputSummary}
          </Text>
        )}
        <View style={styles.metaRow}>
          <Text size='xs' color='tertiary'>{formatTimeAgo(run.createdAt)}</Text>
          {run.creditCost != null && Number(run.creditCost) > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
              <Icon name='bolt' size={9} color={colors.text_color_tertiary} solid />
              <Text size='xs' color='tertiary'>{run.creditCost}</Text>
            </View>
          )}
        </View>
      </View>
    </Pressable>
  );
}

export default React.memo(ToolRecentRunsStrip);
